import React, { useState, useEffect } from 'react';
import { charactersAPI, elementsAPI, pillarsAPI } from '../services/api';
import { Character, Element, Pillar } from '../types';
import { useDataCache } from '../hooks/useDataCache';
import { usePageLoading } from '../hooks/usePageLoading';
import { getImageSource, handleImageErrorWithFallback } from '../utils/imageUtils';
import OptimizedImage from '../components/OptimizedImage';
import LoadingOverlay from '../components/LoadingOverlay';
import './Playmat.css';

interface PlacedCard {
  id: string;
  type: 'character' | 'element' | 'pillar';
  data: Character | Element | Pillar;
  position: { x: number; y: number };
  rotation: number;
  zIndex: number;
}

const Playmat: React.FC = () => {
  // Data fetching with caching
  const {
    data: characters = [],
    isLoading: charactersLoading,
    fetchData: fetchCharacters
  } = useDataCache<Character[]>({
    cacheKey: 'playmat-characters',
    fetchFunction: charactersAPI.getAll,
    cacheDuration: 5 * 60 * 1000,
    autoRefresh: false
  });

  const {
    data: elements = [],
    isLoading: elementsLoading,
    fetchData: fetchElements
  } = useDataCache<Element[]>({
    cacheKey: 'playmat-elements',
    fetchFunction: elementsAPI.getAll,
    cacheDuration: 5 * 60 * 1000,
    autoRefresh: false
  });

  const {
    data: pillars = [],
    isLoading: pillarsLoading,
    fetchData: fetchPillars
  } = useDataCache<Pillar[]>({
    cacheKey: 'playmat-pillars',
    fetchFunction: pillarsAPI.getAll,
    cacheDuration: 5 * 60 * 1000,
    autoRefresh: false
  });

  // Playmat state
  const [placedCards, setPlacedCards] = useState<PlacedCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<PlacedCard | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [nextZIndex, setNextZIndex] = useState(1);
  
  // Playmat dragging state
  const [isPlaymatDragging, setIsPlaymatDragging] = useState(false);
  const [playmatDragStart, setPlaymatDragStart] = useState({ x: 0, y: 0 });
  
  // Better zoom and pan state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [lastPinchDistance, setLastPinchDistance] = useState(0);
  const [isZooming, setIsZooming] = useState(false);

  // Deck states
  const [characterDeck, setCharacterDeck] = useState<Character[]>([]);
  const [elementDeck, setElementDeck] = useState<Element[]>([]);
  const [pillarDeck, setPillarDeck] = useState<Pillar[]>([]);

  // UI states
  const [showDrawPanel, setShowDrawPanel] = useState(true);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [showPanels, setShowPanels] = useState(true);
  
  // Search states - separate for each deck type
  const [characterSearchTerm, setCharacterSearchTerm] = useState('');
  const [elementSearchTerm, setElementSearchTerm] = useState('');
  const [pillarSearchTerm, setPillarSearchTerm] = useState('');
  const [characterSearchResults, setCharacterSearchResults] = useState<Character[]>([]);
  const [elementSearchResults, setElementSearchResults] = useState<Element[]>([]);
  const [pillarSearchResults, setPillarSearchResults] = useState<Pillar[]>([]);
  const [showCharacterSearch, setShowCharacterSearch] = useState(false);
  const [showElementSearch, setShowElementSearch] = useState(false);
  const [showPillarSearch, setShowPillarSearch] = useState(false);
  
  // Save/Load states
  const [savedPlaymats, setSavedPlaymats] = useState<{ name: string; data: PlacedCard[] }[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [playmatName, setPlaymatName] = useState('');

  // Use page loading hook
  const { isLoading: pageLoading } = usePageLoading({
    dependencies: [
      characters !== undefined, // Characters data has been fetched
      elements !== undefined, // Elements data has been fetched
      pillars !== undefined, // Pillars data has been fetched
      !charactersLoading,
      !elementsLoading,
      !pillarsLoading
    ],
    onLoadComplete: () => {
      console.log('✅ Playmat page loaded successfully');
    }
  });

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchCharacters(),
          fetchElements(),
          fetchPillars()
        ]);
      } catch (error) {
        console.error('Error loading playmat data:', error);
      }
    };

    loadData();
  }, [fetchCharacters, fetchElements, fetchPillars]);

  // Load saved playmats from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('aethermap-saved-playmats');
    if (saved) {
      try {
        setSavedPlaymats(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading saved playmats:', error);
      }
    }
  }, []);

  // Initialize decks when data is loaded
  useEffect(() => {
    if ((characters || []).length > 0) {
      setCharacterDeck([...(characters || [])]);
    }
  }, [characters]);

  useEffect(() => {
    if ((elements || []).length > 0) {
      setElementDeck([...(elements || [])]);
    }
  }, [elements]);

  useEffect(() => {
    if ((pillars || []).length > 0) {
      setPillarDeck([...(pillars || [])]);
    }
  }, [pillars]);

  // Search functionality
  const handleSearch = (searchValue: string, deckType: 'character' | 'element' | 'pillar') => {
    let deck: (Character | Element | Pillar)[] = [];
    let setSearchResults: React.Dispatch<React.SetStateAction<any[]>>;
    let setShowSearch: React.Dispatch<React.SetStateAction<boolean>>;
    
    switch (deckType) {
      case 'character':
        setCharacterSearchTerm(searchValue);
        deck = characterDeck;
        setSearchResults = setCharacterSearchResults;
        setShowSearch = setShowCharacterSearch;
        break;
      case 'element':
        setElementSearchTerm(searchValue);
        deck = elementDeck;
        setSearchResults = setElementSearchResults;
        setShowSearch = setShowElementSearch;
        break;
      case 'pillar':
        setPillarSearchTerm(searchValue);
        deck = pillarDeck;
        setSearchResults = setPillarSearchResults;
        setShowSearch = setShowPillarSearch;
        break;
    }
    
    if (!searchValue.trim()) {
      setSearchResults([]);
      setShowSearch(false);
      return;
    }

    const filtered = deck.filter(card => 
      card.name.toLowerCase().includes(searchValue.toLowerCase())
    );
    
    setSearchResults(filtered);
    setShowSearch(filtered.length > 0);
  };

  // Draw a specific card (does NOT remove from deck - can draw same card multiple times)
  const drawSpecificCard = (card: Character | Element | Pillar, deckType: 'character' | 'element' | 'pillar') => {
    // Place card in center of playmat (without removing from deck)
    const newPlacedCard: PlacedCard = {
      id: `${deckType}-${Date.now()}-${Math.random()}`,
      type: deckType,
      data: card,
      position: { x: 400, y: 300 }, // Center position
      rotation: 0,
      zIndex: nextZIndex
    };

    setPlacedCards(prev => [...prev, newPlacedCard]);
    setNextZIndex(prev => prev + 1);
    
    // Removed search clearing - now users can draw the same card multiple times
  };

  // Draw a card from the specified deck (random draw - DOES remove from deck)
  const drawCard = (deckType: 'character' | 'element' | 'pillar') => {
    let deck: (Character | Element | Pillar)[] = [];
    let setDeck: React.Dispatch<React.SetStateAction<any[]>>;

    switch (deckType) {
      case 'character':
        deck = characterDeck;
        setDeck = setCharacterDeck;
        break;
      case 'element':
        deck = elementDeck;
        setDeck = setElementDeck;
        break;
      case 'pillar':
        deck = pillarDeck;
        setDeck = setPillarDeck;
        break;
    }

    if (deck.length === 0) return;

    // Draw a random card
    const randomIndex = Math.floor(Math.random() * deck.length);
    const drawnCard = deck[randomIndex];
    
    // Remove card from deck
    const newDeck = deck.filter((_, index) => index !== randomIndex);
    setDeck(newDeck);

    // Place card in center of playmat
    const newPlacedCard: PlacedCard = {
      id: `${deckType}-${Date.now()}-${Math.random()}`,
      type: deckType,
      data: drawnCard,
      position: { x: 400, y: 300 }, // Center position
      rotation: 0,
      zIndex: nextZIndex
    };

    setPlacedCards(prev => [...prev, newPlacedCard]);
    setNextZIndex(prev => prev + 1);
  };

  // Handle mouse events for dragging cards
  const handleMouseDown = (e: React.MouseEvent, card: PlacedCard) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent playmat dragging when clicking on cards
    setSelectedCard(card);
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    
    // Lock scroll bar in place during dragging
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !selectedCard) return;

    e.preventDefault();
    e.stopPropagation();

    const playmat = e.currentTarget as HTMLElement;
    const rect = playmat.getBoundingClientRect();
    
    // Account for scroll position
    const scrollLeft = playmat.scrollLeft || 0;
    const scrollTop = playmat.scrollTop || 0;
    
    // Account for zoom level in card positioning
    const zoomFactor = 1 / zoomLevel;
    const newX = (e.clientX - rect.left + scrollLeft - dragOffset.x) * zoomFactor;
    const newY = (e.clientY - rect.top + scrollTop - dragOffset.y) * zoomFactor;

    setPlacedCards(prev => prev.map(card =>
      card.id === selectedCard.id
        ? { ...card, position: { x: newX, y: newY } }
        : card
    ));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setSelectedCard(null);
    
    // Restore scrolling and remove scroll bar lock
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e: React.TouchEvent, card: PlacedCard) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent playmat dragging when touching cards
    setSelectedCard(card);
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    
    // Lock scroll bar in place during dragging
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !selectedCard) return;

    e.preventDefault();
    e.stopPropagation();

    const playmat = e.currentTarget as HTMLElement;
    const rect = playmat.getBoundingClientRect();
    const touch = e.touches[0];
    
    // Account for scroll position
    const scrollLeft = playmat.scrollLeft || 0;
    const scrollTop = playmat.scrollTop || 0;
    
    // Account for zoom level in card positioning
    const zoomFactor = 1 / zoomLevel;
    const newX = (touch.clientX - rect.left + scrollLeft - dragOffset.x) * zoomFactor;
    const newY = (touch.clientY - rect.top + scrollTop - dragOffset.y) * zoomFactor;

    setPlacedCards(prev => prev.map(card =>
      card.id === selectedCard.id
        ? { ...card, position: { x: newX, y: newY } }
        : card
    ));
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setSelectedCard(null);
    
    // Restore scrolling and remove scroll bar lock
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  // Playmat dragging handlers
  const handlePlaymatMouseDown = (e: React.MouseEvent) => {
    // Only start playmat dragging if clicking on empty area (not on a card)
    if (e.target === e.currentTarget) {
      e.preventDefault();
      setIsPlaymatDragging(true);
      setPlaymatDragStart({
        x: e.clientX,
        y: e.clientY
      });
    }
  };

  const handlePlaymatMouseMove = (e: React.MouseEvent) => {
    if (!isPlaymatDragging) return;

    e.preventDefault();
    e.stopPropagation();

    const deltaX = e.clientX - playmatDragStart.x;
    const deltaY = e.clientY - playmatDragStart.y;

    // Update pan offset instead of using scroll
    setPanOffset(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));

    setPlaymatDragStart({
      x: e.clientX,
      y: e.clientY
    });
  };

  const handlePlaymatMouseUp = () => {
    setIsPlaymatDragging(false);
    
    // Add a small delay to allow for smooth settling animation
    setTimeout(() => {
      // This ensures the transition applies smoothly after dragging ends
    }, 50);
  };



  const handlePlaymatTouchEnd = () => {
    setIsPlaymatDragging(false);
    
    // Add a small delay to allow for smooth settling animation
    setTimeout(() => {
      // This ensures the transition applies smoothly after dragging ends
    }, 50);
  };

  // Better zoom functionality with proper event isolation
  const handleWheel = (e: React.WheelEvent) => {
    // Only handle wheel events when Shift is held
    if (!e.shiftKey) {
      return; // Allow normal scrolling when not zooming
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.3, Math.min(3, zoomLevel * delta));
    
    if (newZoom !== zoomLevel) {
      setZoomLevel(newZoom);
    }
  };

  const getPinchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) +
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  const handlePlaymatTouchStart = (e: React.TouchEvent) => {
    // Handle pinch gestures
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getPinchDistance(e.touches);
      setLastPinchDistance(distance);
      return;
    }

    // Only start playmat dragging if touching empty area (not on a card)
    if (e.target === e.currentTarget) {
      e.preventDefault();
      setIsPlaymatDragging(true);
      const touch = e.touches[0];
      setPlaymatDragStart({
        x: touch.clientX,
        y: touch.clientY
      });
    }
  };

  const handlePlaymatTouchMove = (e: React.TouchEvent) => {
    // Handle pinch gestures
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getPinchDistance(e.touches);
      
      if (lastPinchDistance > 0) {
        const scale = distance / lastPinchDistance;
        const newZoom = Math.max(0.3, Math.min(3, zoomLevel * scale));
        setZoomLevel(newZoom);
      }
      
      setLastPinchDistance(distance);
      return;
    }

    if (!isPlaymatDragging) return;

    e.preventDefault();
    e.stopPropagation();

    const touch = e.touches[0];
    const deltaX = touch.clientX - playmatDragStart.x;
    const deltaY = touch.clientY - playmatDragStart.y;

    // Update pan offset instead of using scroll
    setPanOffset(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));

    setPlaymatDragStart({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  // Rotate a card
  const rotateCard = (cardId: string) => {
    setPlacedCards(prev => prev.map(card =>
      card.id === cardId
        ? { ...card, rotation: (card.rotation + 90) % 360 }
        : card
    ));
  };

  // Remove a card from the playmat
  const removeCard = (cardId: string) => {
    setPlacedCards(prev => prev.filter(card => card.id !== cardId));
  };

  // Bring card to front
  const bringToFront = (cardId: string) => {
    setPlacedCards(prev => prev.map(card =>
      card.id === cardId
        ? { ...card, zIndex: nextZIndex }
        : card
    ));
    setNextZIndex(prev => prev + 1);
  };

  // Bring card to back
  const bringToBack = (cardId: string) => {
    setPlacedCards(prev => {
      const minZIndex = Math.min(...prev.map(card => card.zIndex));
      return prev.map(card =>
        card.id === cardId
          ? { ...card, zIndex: minZIndex - 1 }
          : card
      );
    });
  };

  // Clear all cards from playmat
  const clearPlaymat = () => {
    setPlacedCards([]);
  };

  // Shuffle all decks
  const shuffleDecks = () => {
    setCharacterDeck([...(characters || [])].sort(() => Math.random() - 0.5));
    setElementDeck([...(elements || [])].sort(() => Math.random() - 0.5));
    setPillarDeck([...(pillars || [])].sort(() => Math.random() - 0.5));
  };

  // Save current playmat
  const savePlaymat = () => {
    if (!playmatName.trim()) return;
    
    const newSavedPlaymat = {
      name: playmatName.trim(),
      data: [...placedCards]
    };
    
    const updatedSavedPlaymats = [...savedPlaymats, newSavedPlaymat];
    setSavedPlaymats(updatedSavedPlaymats);
    localStorage.setItem('aethermap-saved-playmats', JSON.stringify(updatedSavedPlaymats));
    
    setPlaymatName('');
    setShowSaveModal(false);
  };

  // Load a saved playmat
  const loadPlaymat = (savedPlaymat: { name: string; data: PlacedCard[] }) => {
    setPlacedCards(savedPlaymat.data);
    setNextZIndex(Math.max(...savedPlaymat.data.map(card => card.zIndex), 0) + 1);
    setShowLoadModal(false);
  };

  // Delete a saved playmat
  const deleteSavedPlaymat = (index: number) => {
    const updatedSavedPlaymats = savedPlaymats.filter((_, i) => i !== index);
    setSavedPlaymats(updatedSavedPlaymats);
    localStorage.setItem('aethermap-saved-playmats', JSON.stringify(updatedSavedPlaymats));
  };

  const getCardImage = (card: PlacedCard) => {
    switch (card.type) {
      case 'character':
        return getImageSource(card.data as Character, 'character');
      case 'element':
        return getImageSource(card.data as Element, 'element');
      case 'pillar':
        return getImageSource(card.data as Pillar, 'pillar');
      default:
        return '/img/elements/default-0.png';
    }
  };

  const getCardName = (card: PlacedCard) => {
    return card.data.name;
  };

  const getRarityBadge = (card: PlacedCard) => {
    if (card.type !== 'element') return null;
    
    const element = card.data as Element;
    if (!element.rarity) return null;
    
    const rarity = element.rarity.toLowerCase();
    let badgeClass = '';
    let badgeText = '';
    
    switch (rarity) {
      case 'common':
        badgeClass = 'rarity-common';
        badgeText = 'COMMON';
        break;
      case 'uncommon':
        badgeClass = 'rarity-uncommon';
        badgeText = 'UNCOMMON';
        break;
      case 'rare':
        badgeClass = 'rarity-rare';
        badgeText = 'RARE';
        break;
      case 'epic':
        badgeClass = 'rarity-epic';
        badgeText = 'EPIC';
        break;
      case 'legendary':
        badgeClass = 'rarity-legendary';
        badgeText = 'LEGENDARY';
        break;
      case 'mythic':
        badgeClass = 'rarity-mythic';
        badgeText = 'MYTHIC';
        break;
      default:
        badgeClass = 'rarity-common';
        badgeText = element.rarity.toUpperCase();
    }
    
    return { badgeClass, badgeText };
  };

  const getLevelBadge = (card: PlacedCard) => {
    if (card.type !== 'pillar') return null;
    
    const pillar = card.data as Pillar;
    if (!pillar.level) return null;
    
    return { badgeClass: 'level-badge', badgeText: `L${pillar.level}` };
  };

  const isLoading = pageLoading || charactersLoading || elementsLoading || pillarsLoading;

  return (
    <div className="playmat-container">
      {/* Loading Overlay */}
      <LoadingOverlay isLoading={isLoading} message="Loading playmat..." />
      
      {/* Header Controls */}
      <div className="playmat-header">
        <h1>Playmat</h1>
        <div className="playmat-controls">
          <button onClick={clearPlaymat} className="btn btn--clear">
            Clear Playmat
          </button>
          <button onClick={shuffleDecks} className="btn btn--shuffle">
            Shuffle Decks
          </button>
          <button 
            onClick={() => setShowSaveModal(true)} 
            className="btn btn--save"
            disabled={placedCards.length === 0}
          >
            Save Playmat
          </button>
          <button 
            onClick={() => setShowLoadModal(true)} 
            className="btn btn--load"
            disabled={savedPlaymats.length === 0}
          >
            Load Playmat
          </button>
          <button 
            onClick={() => setShowDrawPanel(!showDrawPanel)} 
            className="btn btn--toggle"
          >
            {showDrawPanel ? 'Hide' : 'Show'} Draw Panel
          </button>
          <button 
            onClick={() => setShowSearchPanel(!showSearchPanel)} 
            className="btn btn--toggle"
          >
            {showSearchPanel ? 'Hide' : 'Show'} Search Panel
          </button>
          <button 
            onClick={() => setShowPanels(!showPanels)} 
            className="btn btn--toggle"
          >
            {showPanels ? 'Hide' : 'Show'} All Panels
          </button>
          <button 
            onClick={() => {
              setZoomLevel(1);
              setPanOffset({ x: 0, y: 0 });
            }} 
            className="btn btn--zoom"
            disabled={zoomLevel === 1 && panOffset.x === 0 && panOffset.y === 0}
          >
            Reset View ({Math.round(zoomLevel * 100)}%)
          </button>
        </div>
      </div>

            <div className="playmat-content">
        {/* Draw Panel */}
        {showDrawPanel && (
          <div className={`deck-panel ${!showPanels ? 'hidden' : ''}`}>
            <h2 className="panel-title">Draw Cards</h2>
            <div className="deck-section">
              <h3>Character Deck <span className="deck-count-badge">{characterDeck.length}</span></h3>
              <button 
                onClick={() => drawCard('character')}
                disabled={characterDeck.length === 0}
                className="btn btn--draw"
              >
                Draw from Character Deck
              </button>
            </div>

            <div className="deck-section">
              <h3>Element Deck <span className="deck-count-badge">{elementDeck.length}</span></h3>
              <button 
                onClick={() => drawCard('element')}
                disabled={elementDeck.length === 0}
                className="btn btn--draw"
              >
                Draw from Element Deck
              </button>
            </div>

            <div className="deck-section">
              <h3>Pillar Deck <span className="deck-count-badge">{pillarDeck.length}</span></h3>
              <button 
                onClick={() => drawCard('pillar')}
                disabled={pillarDeck.length === 0}
                className="btn btn--draw"
              >
                Draw from Pillar Deck
              </button>
            </div>
          </div>
        )}

        {/* Search Panel */}
        {showSearchPanel && (
          <div className={`deck-panel ${!showPanels ? 'hidden' : ''}`}>
            <h2 className="panel-title">Search & Select Cards</h2>
            <div className="deck-section">
              <h3>Character Search</h3>
              <div className="deck-controls">
                <input
                  type="text"
                  placeholder="Search characters..."
                  value={characterSearchTerm}
                  onChange={(e) => handleSearch(e.target.value, 'character')}
                  className="deck-search"
                />
              </div>
              {showCharacterSearch && (
                <div className="search-results">
                  {characterSearchResults.map((card) => (
                    <div key={card._id} className="search-result-item">
                      <span className="card-name-small">{card.name}</span>
                      <button
                        onClick={() => drawSpecificCard(card, 'character')}
                        className="btn btn--draw-small"
                      >
                        Draw
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="deck-section">
              <h3>Element Search</h3>
              <div className="deck-controls">
                <input
                  type="text"
                  placeholder="Search elements..."
                  value={elementSearchTerm}
                  onChange={(e) => handleSearch(e.target.value, 'element')}
                  className="deck-search"
                />
              </div>
              {showElementSearch && (
                <div className="search-results">
                  {elementSearchResults.map((card) => (
                    <div key={card._id} className="search-result-item">
                      <span className="card-name-small">{card.name}</span>
                      <button
                        onClick={() => drawSpecificCard(card, 'element')}
                        className="btn btn--draw-small"
                      >
                        Draw
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="deck-section">
              <h3>Pillar Search</h3>
              <div className="deck-controls">
                <input
                  type="text"
                  placeholder="Search pillars..."
                  value={pillarSearchTerm}
                  onChange={(e) => handleSearch(e.target.value, 'pillar')}
                  className="deck-search"
                />
              </div>
              {showPillarSearch && (
                <div className="search-results">
                  {pillarSearchResults.map((card) => (
                    <div key={card._id} className="search-result-item">
                      <span className="card-name-small">{card.name}</span>
                      <button
                        onClick={() => drawSpecificCard(card, 'pillar')}
                        className="btn btn--draw-small"
                      >
                        Draw
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Playmat Area */}
        <div 
          className={`playmat-area ${isDragging ? 'dragging' : ''} ${isPlaymatDragging ? 'playmat-dragging' : ''}`}
          onMouseDown={handlePlaymatMouseDown}
          onMouseMove={isPlaymatDragging ? handlePlaymatMouseMove : handleMouseMove}
          onMouseUp={isPlaymatDragging ? handlePlaymatMouseUp : handleMouseUp}
          onMouseLeave={isPlaymatDragging ? handlePlaymatMouseUp : handleMouseUp}
          onWheel={handleWheel}
          onTouchStart={handlePlaymatTouchStart}
          onTouchMove={isPlaymatDragging ? handlePlaymatTouchMove : handleTouchMove}
          onTouchEnd={isPlaymatDragging ? handlePlaymatTouchEnd : handleTouchEnd}
          style={{ 
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
            transformOrigin: 'center center',
            cursor: isPlaymatDragging ? 'grabbing' : 'grab'
          }}
        >
          {/* Placed Cards */}
                    {placedCards.map(card => (
            <div
              key={card.id}
              className={`card-wrapper ${isDragging && selectedCard?.id === card.id ? 'dragging' : ''}`}
              style={{
                left: card.position.x,
                top: card.position.y,
                zIndex: card.zIndex
              }}
              onMouseDown={(e) => handleMouseDown(e, card)}
              onTouchStart={(e) => handleTouchStart(e, card)}
            >
              <div 
                className="placed-card"
                data-type={card.type}
                style={{
                  transform: `rotate(${card.rotation}deg)`
                }}
              >
                <div className="card-header" data-type={card.type}>
                  {card.type.toUpperCase()}
                </div>
                <div className="card-image">
                  <OptimizedImage
                    src={getCardImage(card)}
                    alt={getCardName(card)}
                    simpleFallback={true}
                    onError={() => console.log(`Failed to load image for ${getCardName(card)}`)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: 'inherit'
                    }}
                  />
                  {/* Rarity Badge for Elements */}
                  {card.type === 'element' && getRarityBadge(card) && (
                    <div className={`rarity-badge ${getRarityBadge(card)?.badgeClass}`}>
                      {getRarityBadge(card)?.badgeText}
                    </div>
                  )}
                  {/* Level Badge for Pillars */}
                  {card.type === 'pillar' && getLevelBadge(card) && (
                    <div className={`level-badge ${getLevelBadge(card)?.badgeClass}`}>
                      {getLevelBadge(card)?.badgeText}
                    </div>
                  )}
                </div>
                <div className="card-name">{getCardName(card)}</div>
              </div>
              
              {/* Card Controls */}
              <div className="card-controls">
                <button 
                  onClick={() => rotateCard(card.id)}
                  className="btn btn--rotate"
                  title="Rotate"
                >
                  🔄
                </button>
                <button 
                  onClick={() => bringToFront(card.id)}
                  className="btn btn--front"
                  title="Bring to Front"
                >
                  ⬆️
                </button>
                <button 
                  onClick={() => bringToBack(card.id)}
                  className="btn btn--back"
                  title="Move to Back"
                >
                  ⬇️
                </button>
                <button 
                  onClick={() => removeCard(card.id)}
                  className="btn btn--remove"
                  title="Remove"
                >
                  ❌
                </button>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {placedCards.length === 0 && (
            <div className="empty-playmat">
              <p>Draw cards from the decks to start playing!</p>
            </div>
                     )}
         </div>
       </div>

       {/* Save Modal */}
       {showSaveModal && (
         <div className="modal-overlay">
           <div className="load-playmat-modal-content">
             <h3>Save Playmat</h3>
             <p>Enter a name for your playmat layout:</p>
             <input
               type="text"
               value={playmatName}
               onChange={(e) => setPlaymatName(e.target.value)}
               placeholder="Enter playmat name..."
               className="modal-input"
               autoFocus
             />
             <div className="modal-buttons">
               <button onClick={savePlaymat} className="btn btn--save" disabled={!playmatName.trim()}>
                 Save
               </button>
               <button onClick={() => setShowSaveModal(false)} className="btn btn--cancel">
                 Cancel
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Load Modal */}
       {showLoadModal && (
         <div className="modal-overlay">
           <div className="load-playmat-modal-content">
             <h3>Load Playmat</h3>
             {savedPlaymats.length === 0 ? (
               <p>No saved playmats found.</p>
             ) : (
               <div className="saved-playmats-list">
                 {savedPlaymats.map((savedPlaymat, index) => (
                   <div key={index} className="saved-playmat-item">
                     <div className="saved-playmat-info">
                       <span className="saved-playmat-name">{savedPlaymat.name}</span>
                       <span className="saved-playmat-count">{savedPlaymat.data.length} cards</span>
                     </div>
                     <div className="saved-playmat-actions">
                       <button 
                         onClick={() => loadPlaymat(savedPlaymat)} 
                         className="btn btn--load-small"
                       >
                         Load
                       </button>
                       <button 
                         onClick={() => deleteSavedPlaymat(index)} 
                         className="btn btn--delete-small"
                       >
                         Delete
                       </button>
                     </div>
                   </div>
                 ))}
               </div>
             )}
             <div className="modal-buttons">
               <button onClick={() => setShowLoadModal(false)} className="btn btn--cancel">
                 Close
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default Playmat; 